import { NodeDefinition } from '../../types'
import { ReminderNodeIcon } from './reminder.icon'
import { ReminderEditorNode } from './reminder.editorNode'
import { ReminderConfigForm } from './reminder.configForm'
import { ReminderExecutionSummary } from './Reminder.executionSummary'
import { DEFAULT_SEND_REMINDER, ReminderTaskName } from './reminder.constants'
import {
  DEFAULT_REMINDER_RECIPIENTS,
  DEFAULT_REMINDER_SUBJECT,
  DEFAULT_REMINDER_TEMPLATE
} from './reminder.template'
import { emailReviewManualTask } from './manualTask'
import { formatSendReminder } from './helpers'
import {
  approversRequiredIfApprovalRequired,
  handlesConnected,
  noUnknownTemplateVariables,
  sendReminderRequired,
  subjectRequired,
  templateRequired,
  toRequired,
  validEmailRecipients
} from './reminder.validators'
import { Duration } from '@sequencehq/utils/dates'
import {
  getClosestMatches,
  semanticTypes
} from 'Automations/nodeDefinitions/semanticTypes'
import { reminderTaskTransformer } from './reminder.taskTransformer'

export type SendReminder = {
  type: 'BEFORE_DUE_DATE' | 'AFTER_DUE_DATE'
  duration: Pick<Duration, 'days'>
}

export type Config = {
  name: string
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  template: string
  sendReminder: SendReminder | undefined
  isApprovalRequired: boolean
  approverUserIds: string[]
}

type Inputs = 'invoiceId' | 'dueDate' | 'customerId'

export type TaskName = ReminderTaskName

export const reminderNodeDefinition: NodeDefinition<Config, Inputs> = {
  name: 'Reminder',
  description: ({ config }) =>
    config.sendReminder ? formatSendReminder(config.sendReminder) : undefined,
  icon: ReminderNodeIcon,
  editorNode: ReminderEditorNode,
  configForm: ReminderConfigForm,
  executionSummary: ReminderExecutionSummary,
  manualTask: {
    type: 'REVIEW_EMAIL',
    ...emailReviewManualTask
  },
  defaultConfig: {
    name: '',
    to: DEFAULT_REMINDER_RECIPIENTS,
    cc: [],
    bcc: [],
    subject: DEFAULT_REMINDER_SUBJECT,
    template: DEFAULT_REMINDER_TEMPLATE,
    sendReminder: DEFAULT_SEND_REMINDER,
    isApprovalRequired: false,
    approverUserIds: []
  },
  inputMapping: ({ upstreamNodes }) => {
    return getClosestMatches<Inputs>(upstreamNodes)({
      invoiceId: semanticTypes.sequence.invoice.id,
      customerId: semanticTypes.sequence.invoice.customerId,
      dueDate: semanticTypes.sequence.invoice.dueDate
    })
  },
  taskTransformer: reminderTaskTransformer,
  validation: {
    node: { ERROR: [], WARN: [] },
    config: {
      name: { ERROR: [], WARN: [] },
      to: { ERROR: [toRequired, validEmailRecipients], WARN: [] },
      cc: { ERROR: [validEmailRecipients], WARN: [] },
      bcc: { ERROR: [validEmailRecipients], WARN: [] },
      subject: {
        ERROR: [subjectRequired, noUnknownTemplateVariables],
        WARN: []
      },
      template: {
        ERROR: [templateRequired, noUnknownTemplateVariables],
        WARN: []
      },
      sendReminder: { ERROR: [sendReminderRequired], WARN: [] },
      isApprovalRequired: { ERROR: [], WARN: [] },
      approverUserIds: {
        ERROR: [approversRequiredIfApprovalRequired],
        WARN: []
      }
    },
    handles: { ERROR: [handlesConnected], WARN: [] }
  }
}
